/**
 * VFD-sized PLP — compact sizer filters the collection grid (Option A).
 * 1. Build collection SKU/handle index via section rendering
 * 2. POST /api/vfd-size
 * 3. Intersect API models with in-collection products
 * 4. Swap #Collection grid with resolved product cards
 */
(function () {
  var PARAM = "vfd_size";
  var INDEX_SECTION = "vfd-collection-index";
  var CARD_SECTION = "vfd-sized-card";
  var PAGE_SIZE = 50;
  var CONCURRENCY = 4;
  var PROTOCOL_LABELS = {
    "modbus-rtu": "Modbus RTU",
    "modbus-tcp": "Modbus TCP",
    "ethernet-ip": "EtherNet/IP",
    profinet: "PROFINET",
    "profibus-dp": "PROFIBUS DP",
    bacnet: "BACnet",
    uss: "USS",
    devicenet: "DeviceNet",
    "cc-link": "CC-Link",
  };

  function $(root, sel) {
    return root.querySelector(sel);
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function normSku(s) {
    return String(s || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
  }

  function fmtNum(n, digits) {
    if (n == null || n === "") return "—";
    var x = Number(n);
    if (isNaN(x)) return escapeHtml(n);
    if (digits == null) return String(Math.round(x * 1000) / 1000);
    return x.toFixed(digits).replace(/\.?0+$/, "");
  }

  function apiHeaders(extra) {
    var h = { Accept: "application/json" };
    if (extra) {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) h[k] = extra[k];
      }
    }
    return h;
  }

  function parseProtocolValue(raw) {
    var v = String(raw == null ? "" : raw).trim();
    return v || null;
  }

  function protocolLabel(id) {
    if (!id) return "";
    return PROTOCOL_LABELS[id] || id;
  }

  function flattenMatches(matches) {
    var all = [];
    var brands = Object.keys(matches || {});
    for (var i = 0; i < brands.length; i++) {
      var brand = brands[i];
      var rows = matches[brand] || [];
      for (var j = 0; j < rows.length; j++) {
        var r = rows[j];
        all.push({
          brand: r.brand || brand,
          model: r.model,
          matchAmps: r.matchAmps != null ? r.matchAmps : r.currentA,
          kw: r.kw,
          hp: r.hp,
          fieldbus: r.fieldbus,
          protocols: r.protocols,
        });
      }
    }
    return all;
  }

  function mapPool(items, limit, worker) {
    var i = 0;
    var results = new Array(items.length);
    function next() {
      if (i >= items.length) return Promise.resolve();
      var idx = i++;
      return Promise.resolve(worker(items[idx], idx)).then(function (val) {
        results[idx] = val;
        return next();
      });
    }
    var runners = [];
    for (var r = 0; r < Math.min(limit, items.length); r++) runners.push(next());
    return Promise.all(runners).then(function () {
      return results;
    });
  }

  function cacheKey(handle, count) {
    return "vfd-plp-index:" + handle + ":" + count;
  }

  function readCache(handle, count) {
    try {
      var raw = sessionStorage.getItem(cacheKey(handle, count));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function writeCache(handle, count, products) {
    try {
      sessionStorage.setItem(cacheKey(handle, count), JSON.stringify(products));
    } catch (e) {
      /* quota — ignore */
    }
  }

  function parseIndexHtml(html) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var el = doc.querySelector("[data-vfd-collection-index]");
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function fetchIndexPage(handle, page) {
    var url =
      "/collections/" +
      encodeURIComponent(handle) +
      "?section_id=" +
      encodeURIComponent(INDEX_SECTION) +
      "&page=" +
      page;
    return fetch(url, { credentials: "same-origin" }).then(function (r) {
      return r.ok ? r.text() : "";
    }).then(function (html) {
      return parseIndexHtml(html);
    });
  }

  function loadCollectionIndex(root) {
    var handle = root.getAttribute("data-collection-handle");
    var count = Number(root.getAttribute("data-products-count") || 0);
    var maxIndex = Number(root.getAttribute("data-max-index") || 1500);
    var cached = readCache(handle, count);
    if (cached) return Promise.resolve({ products: cached, oversized: false });

    if (count > maxIndex) {
      return Promise.resolve({ products: null, oversized: true });
    }

    if (root._vfdIndexPromise) return root._vfdIndexPromise;

    root._vfdIndexPromise = (function () {
      var products = [];
      var pages = Math.max(1, Math.ceil(count / PAGE_SIZE) || 1);

      function loadPage(page) {
        if (page > pages) {
          writeCache(handle, count, products);
          return { products: products, oversized: false };
        }
        return fetchIndexPage(handle, page).then(function (data) {
          if (!data) {
            writeCache(handle, count, products);
            return { products: products, oversized: false };
          }
          if (data.pages) pages = data.pages;
          var batch = data.products || [];
          for (var i = 0; i < batch.length; i++) products.push(batch[i]);
          if (batch.length < PAGE_SIZE || page >= pages) {
            writeCache(handle, count, products);
            return { products: products, oversized: false };
          }
          return loadPage(page + 1);
        });
      }

      return loadPage(1);
    })();

    return root._vfdIndexPromise;
  }

  function scoreSku(productSkus, model, handle) {
    var target = normSku(model);
    if (!target) return 0;
    var parts = String(productSkus || "").split("|");
    var best = 0;
    for (var i = 0; i < parts.length; i++) {
      var sku = normSku(parts[i]);
      if (!sku) continue;
      if (sku === target) best = Math.max(best, 100);
      else if (sku.indexOf(target) !== -1 || target.indexOf(sku) !== -1)
        best = Math.max(best, 80);
    }
    var handleNorm = normSku(handle || "");
    if (handleNorm && handleNorm.indexOf(target) !== -1) best = Math.max(best, 60);
    return best;
  }

  function intersectModels(indexProducts, models) {
    var hits = [];
    var usedHandles = {};
    var unmatchedModels = 0;

    for (var m = 0; m < models.length; m++) {
      var model = models[m].model;
      var best = null;
      var bestScore = 0;
      for (var p = 0; p < indexProducts.length; p++) {
        var prod = indexProducts[p];
        var s = scoreSku(prod.s, model, prod.h);
        if (s > bestScore) {
          bestScore = s;
          best = prod;
        }
      }
      if (best && bestScore >= 60 && !usedHandles[best.h]) {
        usedHandles[best.h] = true;
        hits.push({
          handle: best.h,
          model: model,
          brand: models[m].brand,
          matchAmps: models[m].matchAmps,
          score: bestScore,
        });
      } else if (bestScore < 60) {
        unmatchedModels++;
      }
    }

    return { hits: hits, unmatchedModels: unmatchedModels, totalModels: models.length };
  }

  function fetchCardHtml(handle) {
    var url =
      "/products/" +
      encodeURIComponent(handle) +
      "?section_id=" +
      encodeURIComponent(CARD_SECTION);
    return fetch(url, { credentials: "same-origin" })
      .then(function (r) {
        return r.ok ? r.text() : "";
      })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var root = doc.querySelector("[data-vfd-sized-card]");
        if (!root) {
          // Section wrapping may leave the markup in #shopify-section-*
          var section = doc.querySelector(".shopify-section") || doc.body;
          root = section && section.querySelector("[data-vfd-sized-card]");
        }
        return root ? root.outerHTML : null;
      })
      .catch(function () {
        return null;
      });
  }

  function getListRoot() {
    return document.getElementById("Collection");
  }

  function getPlpContainer() {
    return document.querySelector(".main-plp-container");
  }

  function ensureSizedBar(listRoot) {
    var bar = listRoot.querySelector("[data-vfd-sized-bar]");
    if (bar) return bar;
    bar = document.createElement("div");
    bar.className = "vfd-plp-sized-bar";
    bar.setAttribute("data-vfd-sized-bar", "");
    bar.hidden = true;
    var toolbar = listRoot.querySelector(".plp-toolbar-wrapper");
    if (toolbar) toolbar.after(bar);
    else listRoot.insertBefore(bar, listRoot.firstChild);
    return bar;
  }

  function rememberOriginalGrid(listRoot) {
    if (listRoot._vfdOriginalHtml != null) return;
    var ul = listRoot.querySelector(".product-main-list-container");
    var empty = listRoot.querySelector(".no-products-message");
    var pag = listRoot.querySelector(".pagination");
    listRoot._vfdOriginalHtml = {
      list: ul ? ul.outerHTML : "",
      empty: empty ? empty.outerHTML : "",
      pagination: pag ? pag.outerHTML : "",
      countHtml: (listRoot.querySelector(".filters-toolbar__product-count") || {}).innerHTML || null,
    };
  }

  function removeGridNodes(listRoot) {
    var existingList = listRoot.querySelector(".product-main-list-container");
    var existingEmpty = listRoot.querySelector(".no-products-message");
    var existingPag = listRoot.querySelector(".pagination");
    var existingSized = listRoot.querySelector("[data-vfd-sized-results]");
    if (existingList) existingList.remove();
    if (existingEmpty) existingEmpty.remove();
    if (existingPag) existingPag.remove();
    if (existingSized) existingSized.remove();
  }

  function ensureSizedHost(listRoot, bar) {
    var sized = document.createElement("div");
    sized.setAttribute("data-vfd-sized-results", "");
    bar.after(sized);
    return sized;
  }

  function updateCount(listRoot, text) {
    var el = listRoot.querySelector(".filters-toolbar__product-count");
    if (el) el.textContent = text;
  }

  function renderSizedBar(bar, result, phasesHint, hitCount, totalCount) {
    var pills = [];
    if (result.requiredA != null) {
      pills.push(fmtNum(result.requiredA, 1) + " A");
    }
    if (phasesHint) pills.push(escapeHtml(phasesHint));
    bar.innerHTML =
      '<span class="vfd-plp-sized-bar__label">Sized for</span>' +
      '<div class="vfd-plp-sized-bar__pills">' +
      pills
        .map(function (p) {
          return '<span class="vfd-plp-sized-bar__pill">' + p + "</span>";
        })
        .join("") +
      "</div>" +
      '<div class="vfd-plp-sized-bar__actions">' +
      '<button type="button" class="vfd-plp-sized-bar__btn" data-vfd-edit>Edit</button>' +
      '<button type="button" class="vfd-plp-sized-bar__btn" data-vfd-clear>Clear</button>' +
      "</div>";
    bar.hidden = false;
    bar._hitCount = hitCount;
    bar._totalCount = totalCount;
  }

  function bindBarActions(root, listRoot, bar) {
    var edit = bar.querySelector("[data-vfd-edit]");
    var clear = bar.querySelector("[data-vfd-clear]");
    if (edit) {
      edit.onclick = function () {
        var form = $(root, "[data-vfd-compact-form]");
        if (form) form.scrollIntoView({ behavior: "smooth", block: "center" });
      };
    }
    if (clear) {
      clear.onclick = function () {
        clearSizedState(root, listRoot);
      };
    }
  }

  function showSizedResults(root, listRoot, cardsHtml, result, phasesHint, hitCount, totalCount) {
    rememberOriginalGrid(listRoot);
    removeGridNodes(listRoot);
    var bar = ensureSizedBar(listRoot);
    renderSizedBar(bar, result, phasesHint, hitCount, totalCount);
    var sized = ensureSizedHost(listRoot, bar);

    sized.innerHTML =
      '<ul class="product-main-list-container">' +
      cardsHtml
        .map(function (html) {
          return '<li class="grid__card__item">' + html + "</li>";
        })
        .join("") +
      "</ul>";
    updateCount(
      listRoot,
      hitCount + " of " + totalCount + " drives in this collection fit"
    );

    var plp = getPlpContainer();
    if (plp) plp.classList.add("is-vfd-sized");
    bindBarActions(root, listRoot, bar);
  }

  function showEmptySized(root, listRoot, result, phasesHint, messageHtml) {
    rememberOriginalGrid(listRoot);
    removeGridNodes(listRoot);
    var bar = ensureSizedBar(listRoot);
    renderSizedBar(bar, result, phasesHint, 0, Number(root.getAttribute("data-products-count") || 0));
    var sized = ensureSizedHost(listRoot, bar);
    sized.innerHTML = '<div class="vfd-plp-sized-empty">' + messageHtml + "</div>";
    updateCount(listRoot, "0 matching drives");

    var plp = getPlpContainer();
    if (plp) plp.classList.add("is-vfd-sized");
    bindBarActions(root, listRoot, bar);
  }

  function clearSizedState(root, listRoot) {
    var url = new URL(window.location.href);
    url.searchParams.delete(PARAM);
    if (listRoot._vfdOriginalHtml) {
      var bar = listRoot.querySelector("[data-vfd-sized-bar]");
      var sized = listRoot.querySelector("[data-vfd-sized-results]");
      if (bar) bar.remove();
      if (sized) sized.remove();

      var snap = listRoot._vfdOriginalHtml;
      var toolbar = listRoot.querySelector(".plp-toolbar-wrapper");
      var tmp = document.createElement("div");
      tmp.innerHTML = (snap.list || "") + (snap.empty || "") + (snap.pagination || "");
      var node = tmp.firstChild;
      while (node) {
        var next = node.nextSibling;
        if (toolbar) toolbar.after(node);
        else listRoot.appendChild(node);
        toolbar = node;
        node = next;
      }

      if (snap.countHtml != null) {
        var countEl = listRoot.querySelector(".filters-toolbar__product-count");
        if (countEl) countEl.innerHTML = snap.countHtml;
      }

      listRoot._vfdOriginalHtml = null;
      var plp = getPlpContainer();
      if (plp) plp.classList.remove("is-vfd-sized");
      history.replaceState({}, "", url.toString());
      return;
    }
    window.location.href = url.toString();
  }

  function writeParams(payload) {
    var url = new URL(window.location.href);
    url.searchParams.set(PARAM, btoa(unescape(encodeURIComponent(JSON.stringify(payload)))));
    history.replaceState({}, "", url.toString());
  }

  function readParams() {
    var url = new URL(window.location.href);
    var raw = url.searchParams.get(PARAM);
    if (!raw) return null;
    try {
      return JSON.parse(decodeURIComponent(escape(atob(raw))));
    } catch (e) {
      try {
        return JSON.parse(atob(raw));
      } catch (e2) {
        return null;
      }
    }
  }

  function fillForm(form, payload) {
    if (!form || !payload) return;
    if (payload.power != null) form.elements.power.value = payload.power;
    if (payload.powerUnit) form.elements.powerUnit.value = payload.powerUnit;
    if (payload.fla != null && payload.fla !== "") form.elements.fla.value = payload.fla;
    else form.elements.fla.value = "";
    if (payload.supplyVoltage) form.elements.supplyVoltage.value = String(payload.supplyVoltage);
    if (payload.application) form.elements.application.value = payload.application;
    if (payload.protocol && form.elements.protocol) {
      form.elements.protocol.value = payload.protocol;
    }
    var phases = payload.phases === 1 ? "1" : "3";
    var radio = form.querySelector('input[name="phases"][value="' + phases + '"]');
    if (radio) radio.checked = true;
  }

  function runSize(root, payload, fromUrl) {
    var apiBase = (root.getAttribute("data-api-base") || "").replace(/\/$/, "");
    var vfdUrl = root.getAttribute("data-vfd-url") || "/pages/vfd-sizing-calculator";
    var collectionTitle = root.getAttribute("data-collection-title") || "this collection";
    var form = $(root, "[data-vfd-compact-form]");
    var errEl = $(root, "[data-vfd-compact-error]");
    var submitBtn = $(root, "[data-vfd-compact-submit]");
    var listRoot = getListRoot();

    if (!apiBase) {
      if (errEl) errEl.textContent = "Sizing API is not configured.";
      return;
    }
    if (!listRoot) {
      if (errEl) errEl.textContent = "Product list not found on this page.";
      return;
    }

    if (errEl) errEl.textContent = "";
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = "Finding drives…";
    }

    var body = {
      power: Number(payload.power),
      powerUnit: payload.powerUnit || "hp",
      fla: payload.fla === "" || payload.fla == null ? null : Number(payload.fla),
      supplyVoltage: Number(payload.supplyVoltage),
      phases: Number(payload.phases) === 1 ? 1 : 3,
      serviceFactor: 1.15,
      application: payload.application || "constant",
      ambientC: 40,
      altitudeM: 0,
    };
    var protocol = parseProtocolValue(payload.protocol);
    if (protocol) body.protocols = [protocol];

    if (body.phases === 1 && body.supplyVoltage >= 380) {
      if (errEl) {
        errEl.textContent =
          body.supplyVoltage +
          " V is not a standard single-phase supply. Use 230 V 1-ph, or switch to 3-phase.";
      }
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = "Filter matching drives";
      }
      return;
    }

    // Audit L6-14: above the sized-card index ceiling the PLP cannot match locally; hand the
    // nameplate values to the full calculator, which prefills them from the query string.
    var count = Number(root.getAttribute("data-products-count") || 0);
    var maxIndex = Number(root.getAttribute("data-max-index") || 1500);
    if (count > maxIndex) {
      var q = new URLSearchParams({ from: "plp", v: String(body.supplyVoltage), load: body.application, ph: String(body.phases) });
      q.set(body.powerUnit === "kw" ? "kw" : "hp", String(body.power));
      if (body.fla != null) q.set("fla", String(body.fla));
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = "Filter matching drives"; }
      window.location.href = vfdUrl + (vfdUrl.indexOf("?") >= 0 ? "&" : "?") + q.toString();
      return;
    }

    Promise.all([
      loadCollectionIndex(root),
      fetch(apiBase + "/api/vfd-size", {
        method: "POST",
        headers: apiHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(body),
      }).then(function (r) {
        return r.json().then(function (data) {
          return { ok: r.ok, data: data };
        });
      }),
    ])
      .then(function (pair) {
        var indexResult = pair[0];
        var res = pair[1];

        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = "Filter matching drives";
        }

        if (indexResult.oversized) {
          if (errEl) {
            errEl.innerHTML =
              'This collection is too large to filter in-page. <a href="' +
              escapeHtml(vfdUrl) +
              '">Open the full VFD sizer</a>.';
          }
          return;
        }

        if (!res.ok) {
          if (errEl) errEl.textContent = (res.data && res.data.error) || "Sizing failed.";
          return;
        }

        if (!fromUrl) writeParams(payload);

        var phasesHint =
          body.supplyVoltage +
          " V · " +
          body.phases +
          "-ph" +
          (protocol ? " · " + protocolLabel(protocol) : "");
        var models = flattenMatches(res.data.matches);
        var totalCount = Number(root.getAttribute("data-products-count") || 0);
        var indexProducts = indexResult.products || [];

        if (!models.length) {
          showEmptySized(
            root,
            listRoot,
            res.data,
            phasesHint,
            '<p class="vfd-plp-sized-empty__title">No catalog matches</p>' +
              '<p class="vfd-plp-sized-empty__body">We couldn\'t find drives for this nameplate. Try adjusting FLA or load type, or ' +
              '<a href="' +
              escapeHtml(vfdUrl) +
              '">use the full sizer</a>.</p>'
          );
          listRoot.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }

        var intersected = intersectModels(indexProducts, models);
        if (!intersected.hits.length) {
          showEmptySized(
            root,
            listRoot,
            res.data,
            phasesHint,
            '<p class="vfd-plp-sized-empty__title">No drives in ' +
              escapeHtml(collectionTitle) +
              " fit</p>" +
              '<p class="vfd-plp-sized-empty__body">' +
              intersected.totalModels +
              " catalog match" +
              (intersected.totalModels === 1 ? "" : "es") +
              " elsewhere. " +
              '<a href="' +
              escapeHtml(vfdUrl) +
              '">Open the full VFD sizer</a> to browse all brands.</p>'
          );
          listRoot.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }

        return mapPool(intersected.hits, CONCURRENCY, function (hit) {
          return fetchCardHtml(hit.handle);
        }).then(function (cards) {
          var htmls = cards.filter(Boolean);
          if (!htmls.length) {
            showEmptySized(
              root,
              listRoot,
              res.data,
              phasesHint,
              '<p class="vfd-plp-sized-empty__title">Matches found, but cards failed to load</p>' +
                '<p class="vfd-plp-sized-empty__body">Please refresh and try again, or ' +
                '<a href="' +
                escapeHtml(vfdUrl) +
                '">open the full sizer</a>.</p>'
            );
            return;
          }
          showSizedResults(
            root,
            listRoot,
            htmls,
            res.data,
            phasesHint,
            htmls.length,
            totalCount
          );
          listRoot.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      })
      .catch(function () {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = "Filter matching drives";
        }
        if (errEl) {
          errEl.textContent =
            "Could not size the drive. Check your connection and try again.";
        }
      });
  }

  function init(root) {
    if (root.getAttribute("data-vfd-plp-ready")) return;
    root.setAttribute("data-vfd-plp-ready", "1");

    var form = $(root, "[data-vfd-compact-form]");
    if (!form) return;

    // Warm the index in the background
    loadCollectionIndex(root).catch(function () {});

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var fd = new FormData(form);
      var payload = {
        power: fd.get("power"),
        powerUnit: fd.get("powerUnit") || "hp",
        fla: String(fd.get("fla") || "").trim(),
        supplyVoltage: fd.get("supplyVoltage"),
        phases: fd.get("phases") || "3",
        application: fd.get("application") || "constant",
        protocol: fd.get("protocol") || "",
      };
      runSize(root, payload, false);
    });

    var fromUrl = readParams();
    if (fromUrl) {
      fillForm(form, fromUrl);
      runSize(root, fromUrl, true);
    }
  }

  function boot() {
    document.querySelectorAll("[data-vfd-plp]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (e) {
    var root =
      e.target && e.target.querySelector
        ? e.target.querySelector("[data-vfd-plp]")
        : null;
    if (root) {
      root.removeAttribute("data-vfd-plp-ready");
      init(root);
    }
  });
})();
